import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DocumentText, formatDate } from "./TermsDisclosures";
import { ACCENT, PrimaryButton, errorMessage } from "./shared";
import { termsApi } from "./termsApi";

// Blocks the Artist Studio with a prompt when the legal team has published a newer version of a document the artist must accept
// (FSD 6.8.4). The artist reads the new text, ticks the box and accepts, or leaves the Studio. The server blocks uploading,
// publishing, pricing and withdrawing with TERMS_REACCEPT_REQUIRED until this is done, so skipping the prompt gets nowhere.
// Anything that goes wrong loading it is ignored: the pages and the server rules still work without the prompt.
export default function TermsReacceptGate() {
  const [pending, setPending] = useState([]);
  const [texts, setTexts] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await termsApi.list();
        const need = (data?.documents || []).filter((d) => d.needs_acceptance);
        if (cancelled || !need.length) return;
        setPending(need);
        const loaded = await Promise.all(need.map((d) => termsApi.get(d.code).catch(() => null)));
        if (!cancelled) setTexts(Object.fromEntries(need.map((d, i) => [d.code, loaded[i]?.content || ""])));
      } catch (e) {
        /* the prompt is optional; the server still guards the actions */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!pending.length) return null;

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      await termsApi.accept(pending.map((d) => ({ code: d.code, version: d.version })));
      setPending([]);
    } catch (e) {
      if (e?.response?.status === 409) {
        // The wording changed while this was open: reload to show the newer version.
        window.location.reload();
        return;
      }
      setError(errorMessage(e, "We couldn't save your acceptance. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex [align-items:safe_center] justify-center overflow-y-auto bg-black/60 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="terms-reaccept-title" data-testid="terms-reaccept">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <h2 id="terms-reaccept-title" className="text-xl font-bold text-gray-900">Our terms have been updated</h2>
        <p className="mt-1 text-sm text-gray-600">Please read and accept the updated terms to keep uploading, publishing, pricing and withdrawing.</p>
        <div className="mt-4 max-h-[50vh] overflow-y-auto space-y-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
          {pending.map((d) => (
            <section key={d.code}>
              <h3 className="font-semibold text-gray-900">{d.title}</h3>
              <p className="text-xs text-gray-500 mb-2">Version {d.version} · in effect from {formatDate(d.effective_at)}</p>
              <DocumentText text={texts[d.code] || "Loading…"} testId={`terms-reaccept-text-${d.code}`} />
            </section>
          ))}
        </div>
        <label className="mt-4 flex items-start gap-2 text-sm text-gray-800">
          <input type="checkbox" className="mt-1" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="terms-reaccept-check" style={{ accentColor: ACCENT }} />
          <span>I have read and agree to the updated terms.</span>
        </label>
        {error && <p className="mt-2 text-sm text-red-600" data-testid="terms-reaccept-error">{error}</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          <Link to="/home" className="rounded-full font-semibold text-gray-900 border border-gray-200 bg-white px-6 py-3 hover:bg-gray-50" data-testid="terms-reaccept-exit">Exit Studio</Link>
          <PrimaryButton onClick={accept} disabled={!agreed || busy} data-testid="terms-reaccept-accept">{busy ? "Saving…" : "Accept"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}
